import React from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import Link from '@mui/material/Link';
import propTypes from 'prop-types';
import Avatar from '@/components/common/Avatar';
import SocialAccounts from '@/components/common/SocialAccounts';
import ViewFields from '@/components/common/ViewFields';
import ViewFieldsColumn from '@/components/common/ViewFieldsColumn';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useMobileView from '@/hooks/ui/useMobileView';
import { BioIcon, EmailIcon, WebIcon } from '@/lib/configs/donor-icons';
import translate from '@/lib/intl/translate';
import { DonorConfigType, DonorType } from '@/propTypes';
import { isUserDonorType } from '@/utils/donorType';
import getFullName from '@/utils/getFullName';
import getInitials from '@/utils/getInitials';
import ConstituentPhoneLinkAndIcon from './ConstituentPhoneLinkAndIcon';

const AVATAR_MOB_SIZE = '48px';
const AVATAR_DESKTOP_SIZE = '36px';

/**
 * This is a component for rendering donor cards
 * with two columns and social accounts based on passed configs
 */
function ConstituentInformationCard({ data, firstColConfig, isEditable }) {
  const history = useHistory();
  const {
    avatar,
    biography,
    constituentId,
    emails,
    facebook,
    instagram,
    linkedin,
    orgName,
    phones,
    prefix,
    pronouns,
    preferredFirstName,
    twitter,
    tiktok,
    youtube,
    website,
  } = data;

  const isMobile = useMobileView();

  const avatarSize = React.useMemo(
    () => (isMobile ? AVATAR_MOB_SIZE : AVATAR_DESKTOP_SIZE),
    [isMobile],
  );

  const name = React.useMemo(
    () =>
      orgName || [prefix, getFullName(data, true)].filter(Boolean).join(' '),
    [data],
  );

  const initials = React.useMemo(
    () => getInitials(orgName || getFullName(data), isUserDonorType(data)),
    [data],
  );

  const onNoDataLabelClick = (key) => {
    history.push(
      `${generatePath(AuthRoutes.CONSTITUENT_EDIT, {
        constituentId,
      })}#${key}`,
    );
  };

  return (
    <div data-testid="constituent-information-card">
      {name && (
        <div className="mb-5 flex items-center">
          <Avatar size={avatarSize} src={avatar}>
            {initials}
          </Avatar>
          <div className="pl-4">
            <div className="flex flex-row items-center ">
              <div className="text-2xl">{name}</div>
              {pronouns && (
                <div className="self-end pl-3 pb-[2.1px] text-sm font-normal text-grey-900">
                  {pronouns}
                </div>
              )}
            </div>
            <div className="text-grey-900 type-body3">
              {preferredFirstName &&
                `${translate('DONOR_PREFERS')}: ${preferredFirstName}`}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2">
        <ViewFieldsColumn
          config={firstColConfig}
          data={data}
          iconContainerWidth={avatarSize}
          onNoDataLabelClick={onNoDataLabelClick}
          showNoDataLabel={isEditable}
        />
        <div className="flex flex-col justify-between md:flex-row">
          <div className="flex flex-col gap-2 pr-0 pb-6 md:pr-4 md:pb-0">
            {emails?.map((email) => (
              <div className="flex" key={email.email}>
                {EmailIcon}
                <Link className="ml-4" href={`mailto:${email.email}`}>
                  {email.email}
                </Link>
              </div>
            ))}
            {phones?.map((phone) => (
              <ConstituentPhoneLinkAndIcon
                className="flex"
                phoneObject={phone}
                key={phone.number}
              />
            ))}
            {website && (
              <div className="flex">
                {WebIcon}
                <Link className="ml-4" href={website}>
                  {website}
                </Link>
              </div>
            )}
          </div>
          <SocialAccounts
            facebook={facebook}
            instagram={instagram}
            linkedin={linkedin}
            twitter={twitter}
            youtube={youtube}
            tiktok={tiktok}
            className="flex justify-evenly pb-2 md:flex-col md:gap-2"
          />
        </div>
      </div>
      <div className="w-full pt-6 md:pt-1">
        <ViewFields
          icon={BioIcon}
          iconContainerWidth={avatarSize}
          onNoDataLabelClick={onNoDataLabelClick}
          showNoDataLabel={isEditable}
          data={[{ value: biography }]}
        />
      </div>
    </div>
  );
}

ConstituentInformationCard.propTypes = {
  data: DonorType,
  firstColConfig: propTypes.arrayOf(propTypes.shape(DonorConfigType))
    .isRequired,
  isEditable: propTypes.bool,
};

ConstituentInformationCard.defaultProps = {
  data: {},
  isEditable: false,
};

export default ConstituentInformationCard;
